import React, { useEffect, useState } from 'react'
import { ArrowUpDown } from 'lucide-react'

import { nutrimaticLabels } from '@/nutrimatic'
import { getActiveChainId } from '@/config/active'
import type { SwapControllerResult } from '../../../../features/swap/useSwapController'
import { Field } from '../../../../domain/trading'
import {
  getDirectedSpotQuote,
  type OnChainPairData,
} from '../../../../features/market/useOnChainPairData'
import { useIndexedPoolForCurrencies } from '../../../../features/market/useIndexedPoolForCurrencies'
import { resolveMarketPairLegs } from '../../../../features/market/resolveMarketPairLegs'
import { usePairMarketData } from '../../../../features/market/usePairMarketData'
import { PriceImpactReadout } from '../../PriceImpactReadout'

/** Telemetría de cotización integrada al pie del trade stack (mismo card que From/To). */
export function MobileSwapQuotePreview({ controller }: { controller: SwapControllerResult }) {
  const { priceImpactDisplay, priceImpactLimitIsPairSpecific, trade } = controller

  if (!trade) {
    return (
      <div className="nm-mobile-simple__trade-telemetry">
        <p className="nm-mobile-simple__trade-telemetry-idle">
          {nutrimaticLabels.mobileSimple.quoteIdle}
        </p>
      </div>
    )
  }

  return (
    <div className="nm-mobile-simple__trade-telemetry">
      <p className="nm-mobile-simple__trade-telemetry-receive">
        {nutrimaticLabels.mobileSimple.approxReceive(
          trade.outputAmount.toSignificant(6),
          trade.outputAmount.currency.symbol ?? '',
        )}
      </p>
      <div className="nm-mobile-simple__trade-telemetry-readout">
        <PriceImpactReadout
          display={priceImpactDisplay}
          limitIsPairSpecific={priceImpactLimitIsPairSpecific}
          variant="telemetry"
        />
      </div>
    </div>
  )
}

/** Rate hero: market base/quote — independent of trade direction invert. */
export function MobileSwapRateHero({
  controller,
  pairData,
}: {
  controller: SwapControllerResult
  pairData: OnChainPairData
}) {
  const chainId = getActiveChainId()
  const pay = controller.currencies[Field.INPUT]
  const receive = controller.currencies[Field.OUTPUT]
  const indexedPool = useIndexedPoolForCurrencies(pay, receive)
  const { base, quote, pairLabel } = resolveMarketPairLegs(pay, receive, indexedPool)
  const marketData = usePairMarketData(pay, receive)
  const [showInverted, setShowInverted] = useState(false)
  const [usdPrimary, setUsdPrimary] = useState(true)
  const hasUsd = Boolean(marketData.basePriceUsd && marketData.basePriceUsd !== '—')
  const usdLine = hasUsd ? nutrimaticLabels.baseUsdPrice(marketData.basePriceUsd) : null

  // Reset display prefs only when the market pair identity changes — not when trade flips.
  useEffect(() => {
    setShowInverted(false)
    setUsdPrimary(true)
  }, [pairLabel, indexedPool?.pairAddress])

  const spotPay = showInverted ? quote : base
  const spotReceive = showInverted ? base : quote

  const spotQuote = React.useMemo(
    () => getDirectedSpotQuote(pairData, spotPay, spotReceive, chainId),
    [chainId, pairData, spotPay, spotReceive],
  )

  if (!base || !quote) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (pairData.loading) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        {nutrimaticLabels.mobileSimple.rateIdle}
      </p>
    )
  }

  if (!pairData.exists || !spotQuote) {
    return (
      <p className="nm-mobile-simple__rate-hero nm-mobile-simple__rate-hero--idle">
        Sin pool on-chain para este par
      </p>
    )
  }

  const showUsdPrimary = hasUsd && usdPrimary
  const displayToggle = hasUsd ? (
    <button
      type="button"
      className="nm-mobile-simple__rate-hero-toggle"
      onClick={() => setUsdPrimary((prev) => !prev)}
      aria-label={nutrimaticLabels.swapPriceDisplayToggle}
      aria-pressed={usdPrimary}
      title={nutrimaticLabels.swapPriceDisplayToggle}
    >
      <ArrowUpDown className="size-2.5" aria-hidden />
    </button>
  ) : null

  const pairLine = (
    <button
      type="button"
      className={
        showUsdPrimary
          ? 'nm-mobile-simple__rate-hero-usd nm-mobile-simple__rate-hero-pair-btn'
          : 'nm-mobile-simple__rate-hero-main nm-mobile-simple__rate-hero-pair-btn'
      }
      onClick={() => setShowInverted((value) => !value)}
      title={nutrimaticLabels.mobileSimple.invertRate}
    >
      1 {spotQuote.paySymbol} = {spotQuote.rateAmount} {spotQuote.rateUnitSymbol}
      <span className="nm-mobile-simple__rate-hero-hint" aria-hidden>
        ↻
      </span>
    </button>
  )

  return (
    <div className="nm-mobile-simple__rate-hero">
      {showUsdPrimary ? (
        <>
          <span
            className="nm-mobile-simple__rate-hero-main nm-mobile-simple__rate-hero-main--usd"
            aria-label={nutrimaticLabels.baseUsdPriceAria}
          >
            {marketData.basePriceUsd}
          </span>
          <div className="nm-mobile-simple__rate-hero-secondary-row">
            {pairLine}
            {displayToggle}
          </div>
        </>
      ) : (
        <>
          {pairLine}
          {usdLine ? (
            <div className="nm-mobile-simple__rate-hero-secondary-row">
              <span className="nm-mobile-simple__rate-hero-usd" aria-label={nutrimaticLabels.baseUsdPriceAria}>
                {usdLine}
              </span>
              {displayToggle}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
